import { useMemo } from 'react';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { CANCER_TYPE_COMBOBOX_TEXT } from '@/constants/cancerTypeCombobox';
import { segmentClient } from '@/util/analytics';

export type CancerTypeLookup = Map<
  number,
  { label: string; shortLabel?: string | null; value: string; synonyms: string[] }
>;

type CancerTypeComboboxProps = {
  className?: string;
  focusId: string;
  setFocusId: (value: string) => void;
  selectedCancerTypeLabel: string;
  cancerTypeLookup?: CancerTypeLookup;
  colors: { typography: Record<number, string> };
  bottom: number;
};

function track(event: string, properties?: Parameters<typeof segmentClient.track>[1]) {
  Promise.resolve(
    properties ? segmentClient.track(event, properties) : segmentClient.track(event)
  ).catch(() => undefined);
}

/**
 * Directory wrapper around the shared `Combobox`: maps the Directory's lookup and
 * focus props onto the picker and reports the Directory's cancer type analytics events.
 *
 * It draws no search icon. The search box above already carries one and the Inquiry Library's
 * cancer type filter omits it too. The treatment dropdown beside it is drawn with the same
 * picker and the same props, so the two cannot differ in icon, padding or behavior.
 */
export function CancerTypeCombobox({
  className,
  focusId,
  setFocusId,
  selectedCancerTypeLabel,
  cancerTypeLookup,
  colors,
  bottom,
}: CancerTypeComboboxProps) {
  const options = useMemo<ComboboxOption[]>(
    () =>
      [...(cancerTypeLookup?.entries() ?? [])].map(([id, focus]) => ({
        value: String(id),
        label: focus.label,
        shortLabel: focus.shortLabel ?? '',
        synonyms: focus.synonyms,
      })),
    [cancerTypeLookup]
  );

  const analytics = useMemo(
    () => ({
      onViewed: () => track(ANALYTICS_EVENTS.CANCER_TYPE_VIEWED),
      onSearched: (searchTerm: string) =>
        track(ANALYTICS_EVENTS.CANCER_TYPE_SEARCHED, { search_term: searchTerm }),
      onSelected: (cancerTypeId: string) =>
        track(ANALYTICS_EVENTS.CANCER_SELECTED, {
          cancer_type: cancerTypeLookup?.get(Number(cancerTypeId)) || '',
        }),
    }),
    [cancerTypeLookup]
  );

  return (
    <Combobox
      {...CANCER_TYPE_COMBOBOX_TEXT}
      className={className}
      value={focusId}
      onChange={setFocusId}
      options={options}
      selectedLabel={selectedCancerTypeLabel}
      textColor={colors.typography[900]}
      bottomInset={bottom}
      showSearchIcon={false}
      analytics={analytics}
    />
  );
}
